import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { renderToStaticMarkup } from "react-dom/server";
import { apiClient } from "services/Api";

const STEP_OPTIONS = [
  "application_cleaner",
  "requirements_builder",
  "context_agent",
  "narrative_builder",
  "writer_agent",
  "qa_agent",
  "commit_text",
];

const prettyJson = (value) => JSON.stringify(value ?? {}, null, 2);
const markdownToHtml = (markdown) =>
  renderToStaticMarkup(
    <ReactMarkdown remarkPlugins={[remarkGfm]}>
      {markdown || ""}
    </ReactMarkdown>,
  );

const StreamPilotPanel = () => {
  const { id } = useParams();
  const [fieldId, setFieldId] = useState("");
  const [customInstructions, setCustomInstructions] = useState("");
  const [maxIterations, setMaxIterations] = useState(3);
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [job, setJob] = useState(null);
  const [globalState, setGlobalState] = useState(null);
  const [agentMemoryJson, setAgentMemoryJson] = useState("{}");
  const [agentMemoryError, setAgentMemoryError] = useState("");
  const [memoryFiles, setMemoryFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [revisionPolling, setRevisionPolling] = useState(false);
  const [revisionBaselineVersionCount, setRevisionBaselineVersionCount] = useState(0);
  const [revisionBaselineHistoryCount, setRevisionBaselineHistoryCount] = useState(0);
  const [stepToRun, setStepToRun] = useState("application_cleaner");
  const [revisionMessage, setRevisionMessage] = useState("");

  const applicationId = useMemo(() => Number(id), [id]);

  const loadState = async () => {
    const response = await apiClient.get(
      `/stream-pilot/application/${applicationId}/state/`,
    );
    setGlobalState(response.data);
  };

  const loadMemoryFiles = async () => {
    const response = await apiClient.get(
      `/stream-pilot/application/${applicationId}/memory/files/`,
    );
    setMemoryFiles(response.data?.files || []);
  };

  const loadJobs = async (preferredJobId = null) => {
    const response = await apiClient.get("/stream-pilot/jobs/", {
      params: { application_id: applicationId },
    });
    const loadedJobs = response.data || [];
    setJobs(loadedJobs);

    if (loadedJobs.length === 0) {
      setSelectedJobId("");
      setJob(null);
      return null;
    }

    const targetId = Number(
      preferredJobId || selectedJobId || (job ? job.id : loadedJobs[0].id),
    );
    const selected = loadedJobs.find((item) => item.id === targetId) || loadedJobs[0];
    setSelectedJobId(String(selected.id));
    setJob(selected);
    return selected;
  };

  const loadJob = async (jobId) => {
    const response = await apiClient.get(`/stream-pilot/jobs/${jobId}/`);
    const next = response.data;
    setJob(next);
    setJobs((current) => {
      if (!current.some((item) => item.id === next.id)) {
        return [next, ...current];
      }
      return current.map((item) => (item.id === next.id ? next : item));
    });
    return next;
  };

  const triggerGenerate = async () => {
    if (!fieldId) return;
    setIsSubmitting(true);
    try {
      const response = await apiClient.post("/stream-pilot/generate-field/", {
        application_id: applicationId,
        field_id: fieldId,
        max_iterations: maxIterations,
        custom_instructions: customInstructions || undefined,
      });
      const nextJob = response.data;
      setJob(nextJob);
      setSelectedJobId(String(nextJob.id));
      await loadJobs(nextJob.id);
      setPolling(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const runStep = async (forcedStep = null) => {
    if (!job) return;
    const step = forcedStep || stepToRun;
    setIsSubmitting(true);
    try {
      await apiClient.post(`/stream-pilot/jobs/${job.id}/run-step/`, {
        step,
      });
      setStepToRun(step);
      setPolling(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resumeFailedJob = async () => {
    if (!job || job.status !== "FAILED") return;
    setIsSubmitting(true);
    try {
      await apiClient.post(`/stream-pilot/jobs/${job.id}/resume/`);
      setPolling(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendRevisionMessage = async () => {
    if (!job || !revisionMessage.trim()) return;
    setIsSubmitting(true);
    try {
      await apiClient.post(`/stream-pilot/jobs/${job.id}/revision-chat/`, {
        message: revisionMessage.trim(),
      });
      setRevisionBaselineVersionCount(job?.text_output_versions?.length || 0);
      setRevisionBaselineHistoryCount(job?.revision_history?.length || 0);
      setRevisionPolling(true);
      setRevisionMessage("");
      setPolling(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateAgentMemory = async (state) => {
    if (!globalState) return;
    const response = await apiClient.patch(
      `/stream-pilot/application/${applicationId}/state/`,
      { state },
    );
    setGlobalState(response.data);
    setAgentMemoryJson(prettyJson(response.data?.state || {}));
  };

  const saveAgentMemoryJson = async () => {
    try {
      const parsed = JSON.parse(agentMemoryJson);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setAgentMemoryError("Agent memory deve ser um objeto JSON.");
        return;
      }
      setAgentMemoryError("");
      await updateAgentMemory(parsed);
    } catch (error) {
      setAgentMemoryError(`JSON inválido: ${error.message}`);
    }
  };

  useEffect(() => {
    if (!applicationId) return;
    loadState();
    loadJobs();
    loadMemoryFiles();
  }, [applicationId]);

  useEffect(() => {
    if (!globalState) return;
    setAgentMemoryJson(prettyJson(globalState?.state || {}));
  }, [globalState]);

  useEffect(() => {
    if (!polling || !job) return;
    let timer = null;
    const poll = async () => {
      const updated = await loadJob(job.id);
      await loadJobs(updated.id);
      await loadState();
      await loadMemoryFiles();

      const updatedVersionCount = updated?.text_output_versions?.length || 0;
      const updatedHistoryCount = updated?.revision_history?.length || 0;
      const revisionStateIsEmpty =
        !updated?.revision_state ||
        Object.keys(updated.revision_state || {}).length === 0;
      const revisionHasProducedUpdates =
        updatedVersionCount > revisionBaselineVersionCount ||
        updatedHistoryCount > revisionBaselineHistoryCount;
      const revisionFinished =
        revisionPolling &&
        ["COMPLETED", "FAILED"].includes(updated.status) &&
        (
          updated.status === "FAILED" ||
          (
            updated.current_step === "commit_text" &&
            revisionStateIsEmpty &&
            revisionHasProducedUpdates
          )
        );

      if (revisionFinished) {
        setRevisionPolling(false);
        setPolling(false);
        return;
      }

      if (!revisionPolling && ["COMPLETED", "FAILED"].includes(updated.status)) {
        setPolling(false);
      }
    };
    timer = setInterval(poll, 3000);
    return () => clearInterval(timer);
  }, [
    polling,
    job,
    revisionPolling,
    revisionBaselineVersionCount,
    revisionBaselineHistoryCount,
  ]);

  const uploadMemoryFile = async () => {
    if (!selectedFile) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      await apiClient.post(
        `/stream-pilot/application/${applicationId}/memory/files/`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      setSelectedFile(null);
      await loadMemoryFiles();
      await loadState();
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteMemoryFile = async (fileId) => {
    setIsSubmitting(true);
    try {
      await apiClient.delete(
        `/stream-pilot/application/${applicationId}/memory/files/${fileId}/`,
      );
      await loadMemoryFiles();
    } finally {
      setIsSubmitting(false);
    }
  };

  const reloadAgentMemory = async () => {
    await loadState();
  };

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h2 className="mb-0">StreamPilot</h2>
        <Link
          className="btn btn-outline-primary"
          to={`/stream-pilot/${applicationId}/subagents`}
        >
          Testar subagentes
        </Link>
      </div>

      <div className="mt-4">
        <h4>Agent Jobs</h4>
        <div className="d-flex gap-2">
          <select
            className="form-select"
            value={selectedJobId}
            onChange={async (e) => {
              const value = e.target.value;
              setSelectedJobId(value);
              if (value) {
                await loadJob(value);
              } else {
                setJob(null);
              }
            }}
          >
            <option value="">Selecionar job...</option>
            {jobs.map((item) => (
              <option key={item.id} value={item.id}>
                #{item.id} | {item.field_id} | {item.status} | {item.current_step}
              </option>
            ))}
          </select>
          <button className="btn btn-outline-secondary" onClick={() => loadJobs()}>
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-4">
        <h4>Novo Job</h4>
        <div className="mb-3">
          <label>Field ID</label>
          <input
            className="form-control"
            value={fieldId}
            onChange={(e) => setFieldId(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label>Custom Instructions</label>
          <textarea
            className="form-control"
            rows="4"
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label>Max Iterations</label>
          <input
            className="form-control"
            type="number"
            min="1"
            max="5"
            value={maxIterations}
            onChange={(e) => setMaxIterations(Number(e.target.value))}
          />
        </div>
        <button
          className="btn btn-primary"
          disabled={isSubmitting}
          onClick={triggerGenerate}
        >
          Generate Field
        </button>
        {job && (
          <button
            className="btn btn-outline-secondary ms-2"
            disabled={isSubmitting}
            onClick={triggerGenerate}
          >
            Regenerate
          </button>
        )}
      </div>

      {job && (
        <div className="mt-4">
          <h4>Job Status</h4>
          <div>Status: {job.status}</div>
          <div>Step: {job.current_step}</div>
          <div>
            Iterations: {job.qa_iteration}/{job.max_qa_iterations}
          </div>
          {job.status === "FAILED" && (
            <button
              className="btn btn-warning mt-2"
              disabled={isSubmitting}
              onClick={resumeFailedJob}
            >
              Resume Job
            </button>
          )}
          <div className="mt-3">
            <label>Run Individual Step</label>
            <select
              className="form-select"
              value={stepToRun}
              onChange={(e) => setStepToRun(e.target.value)}
            >
              {STEP_OPTIONS.map((step) => (
                <option key={step} value={step}>
                  {step}
                </option>
              ))}
            </select>
            <button
              className="btn btn-outline-primary mt-2"
              disabled={isSubmitting}
              onClick={() => runStep()}
            >
              Run Step
            </button>
          </div>
        </div>
      )}

      <div className="mt-4">
        <h4>Job Text Output</h4>
        <Editor
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          value={markdownToHtml(job?.text_output || "_Sem conteúdo._")}
          disabled
          init={{
            license_key: "gpl",
            height: 320,
            menubar: false,
            toolbar: false,
            statusbar: false,
            branding: false,
            promotion: false,
            readonly: true,
            content_style:
              "body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }",
          }}
        />
      </div>

      {job && (
        <div className="mt-4">
          <h4>Revision Chat Loop</h4>
          <textarea
            className="form-control"
            rows="4"
            placeholder="Indica alterações ao texto gerado (estilo, foco, estrutura, factos em falta, etc.)"
            value={revisionMessage}
            onChange={(e) => setRevisionMessage(e.target.value)}
          />
          <button
            className="btn btn-outline-primary mt-2"
            disabled={isSubmitting || !revisionMessage.trim()}
            onClick={sendRevisionMessage}
          >
            Enviar Revisão
          </button>
        </div>
      )}

      {job?.text_output_versions?.length > 0 && (
        <div className="mt-4">
          <h4>Versões Finais</h4>
          {job.text_output_versions.map((version) => (
            <div key={version.version} className="border rounded p-3 mb-3">
              <div>
                <strong>Versão {version.version}</strong> | {version.source}
              </div>
              <div className="small text-muted mb-2">
                {version.created_at
                  ? new Date(version.created_at).toLocaleString()
                  : ""}
              </div>
              <Editor
                tinymceScriptSrc="/tinymce/tinymce.min.js"
                value={markdownToHtml(version.text || "_Sem conteúdo._")}
                disabled
                init={{
                  license_key: "gpl",
                  height: 240,
                  menubar: false,
                  toolbar: false,
                  statusbar: false,
                  branding: false,
                  promotion: false,
                  readonly: true,
                }}
              />
            </div>
          ))}
        </div>
      )}

      {job?.qa_pack?.issues?.length > 0 && (
        <div className="mt-4">
          <h4>QA Issues</h4>
          {job.qa_pack.issues.map((issue, index) => (
            <div key={`${issue.rule}-${index}`} className="mb-2">
              <div>{issue.rule}</div>
              <div>{issue.evidence}</div>
              <div>Severity: {issue.severity}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <h4>Agent Memory (JSON Editor)</h4>
        <textarea
          className="form-control"
          rows="10"
          value={agentMemoryJson}
          onChange={(e) => setAgentMemoryJson(e.target.value)}
        />
        {agentMemoryError && (
          <div className="text-danger mt-2">{agentMemoryError}</div>
        )}
        <div className="d-flex gap-2 mt-2">
          <button className="btn btn-outline-primary" onClick={saveAgentMemoryJson}>
            Save Memory
          </button>
          <button className="btn btn-outline-secondary" onClick={reloadAgentMemory}>
            Reload
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              try {
                const parsed = JSON.parse(agentMemoryJson);
                setAgentMemoryJson(prettyJson(parsed));
                setAgentMemoryError("");
              } catch (error) {
                setAgentMemoryError(`JSON inválido: ${error.message}`);
              }
            }}
          >
            Format JSON
          </button>
        </div>
      </div>

      <div className="mt-4">
        <h4>Agent Memory Files</h4>
        <div className="small text-muted mb-2">
          Vector Store: {globalState?.vector_store_id || "-"}
        </div>
        <div className="d-flex gap-2 align-items-center mb-3">
          <input
            type="file"
            className="form-control"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
          />
          <button
            className="btn btn-outline-primary"
            disabled={isSubmitting || !selectedFile}
            onClick={uploadMemoryFile}
          >
            Upload
          </button>
          <button
            className="btn btn-outline-secondary"
            disabled={isSubmitting}
            onClick={loadMemoryFiles}
          >
            Refresh
          </button>
        </div>
        {memoryFiles.length === 0 && (
          <div className="text-muted">Sem ficheiros no vector store.</div>
        )}
        {memoryFiles.map((file) => (
          <div
            key={file.id}
            className="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
          >
            <div>
              <div>{file.filename || file.id}</div>
              <div className="small text-muted">
                {file.bytes != null ? `${file.bytes} bytes` : "size n/a"}
              </div>
            </div>
            <button
              className="btn btn-sm btn-outline-danger"
              disabled={isSubmitting}
              onClick={() => deleteMemoryFile(file.id)}
            >
              Delete
            </button>
          </div>
        ))}
      </div>

      {job?.runs?.length > 0 && (
        <div className="mt-4">
          <h4>Agent Runs</h4>
          {job.runs.map((run) => (
            <div key={run.id} className="border rounded p-3 mb-3">
              <div>
                <strong>Run #{run.id}</strong> | Step: {run.step} | Status: {run.status}
              </div>
              <div className="small text-muted">
                {new Date(run.created_at).toLocaleString()}
              </div>
              <button
                className="btn btn-sm btn-outline-primary mt-2"
                disabled={isSubmitting}
                onClick={() => runStep(run.step)}
              >
                Run This Step Again
              </button>
              <div className="mt-3">
                <label>Input Payload</label>
                <textarea
                  className="form-control"
                  rows="6"
                  readOnly
                  value={prettyJson(run.input_payload || {})}
                />
              </div>
              <div className="mt-3">
                <label>Output Payload</label>
                <textarea
                  className="form-control"
                  rows="8"
                  readOnly
                  value={prettyJson(run.output_payload || {})}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StreamPilotPanel;
